import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Link } from 'react-router-dom';
import StickyBox from 'react-sticky-box';
import bigImg from '../../doc/img/header/sider-top3.jpg';
import { ARTICLE_HEIGHT } from '../../utils/constants';
import useMediaQuery from '../../utils/useMediaQuery';
import HorizontalIframeHomepage from '../AdserverIframe/Homepage/HorizontalIframe';
import SquareIframeHomepage from '../AdserverIframe/Homepage/SquareIframe';
import CalendarWidget from '../CalendarWidget';
import ContentList from '../ContentList';
import SidebarArticles from '../SidebarArticles/SidebarArticles';

/**
 * HomePageLayout Component
 * Renders the main homepage gallery with sections for the main article,
 * news, offers, and additional article widgets.
 */
const HomePageLayout = ({
  mainArticle,
  newsArticles,
  publisherArticles,
  latestOffers,
  buttonText,
  wissenArticles,
  gtTippsArticles,
  projekteArticles,
  calendarContent,
  squirrelNewsArticles,
}) => {
  const [articleLimit, setArticleLimit] = useState(7);
  const articlesContainerRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 992px)');
  const { i18n } = useTranslation();

  useEffect(() => {
    const updateArticleLimit = () => {
      if (
        newsArticles &&
        gtTippsArticles &&
        projekteArticles &&
        wissenArticles &&
        squirrelNewsArticles.squirrelNews.length > 0 &&
        articlesContainerRef.current &&
        !isMobile
      ) {
        const containerHeight = articlesContainerRef.current.clientHeight;
        const newArticleLimit = Math.floor(containerHeight / ARTICLE_HEIGHT);

        if (newArticleLimit !== articleLimit) {
          setArticleLimit(newArticleLimit);
        }
      }
    };

    updateArticleLimit();

    window.addEventListener('resize', updateArticleLimit);
    return () => {
      window.removeEventListener('resize', updateArticleLimit);
    };
  }, [
    newsArticles,
    squirrelNewsArticles,
    gtTippsArticles,
    projekteArticles,
    wissenArticles,
    articlesContainerRef,
    isMobile,
  ]);

  const fetchArticleHomepageHook = useCallback(() => {
    setArticleLimit(articleLimit + 2);
  }, [articleLimit]);

  return (
    <div className="post_gallary_area theme3_bg mb40 padding-top-30">
      <div className="container">
        <div className="row">
          {/* Main Content Area */}
          <div className="col-md-12 col-lg-9 d-flex flex-column">
            {/* Horizontal Banner for non-mobile devices */}
            {!isMobileOnly && <HorizontalIframeHomepage />}
            <div className="main-article-container">
              {/* Main Article */}
              {mainArticle && (
                <Link to={`/${mainArticle.type}/${mainArticle.slug}`}>
                  <div className="single_post post_type6 border-radious7 xs-mb30 main_article_homepage">
                    <div className="post_img gradient1">
                      <div className="img_wrap">
                        <img
                          className="lazyLoad wrapper__img"
                          src={
                            mainArticle.main_image
                              ? `${process.env.REACT_APP_CMS_URL_IMAGE}${mainArticle.main_image.url}`
                              : bigImg
                          }
                          alt="thumb"
                          effect="blur"
                        />
                      </div>
                    </div>
                    <div className="single_post_text">
                      <h2 className="single_post_title">{mainArticle.title}</h2>
                      <div className="space-10" />
                      <p className="post-p">{mainArticle.description}</p>
                      <div className="meta">
                        <span>
                          {moment(mainArticle.publishAt)
                            .locale(i18n.language)
                            .format('LL')}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}
            </div>

            <div className="space-10" />
            {!isMobileOnly && <HorizontalIframeHomepage />}

            {/* Business News Section */}
            <ContentList
              isHomePage
              articleLimit={articleLimit}
              publisherArticles={publisherArticles}
              buttonText={buttonText}
              fetchContentHook={fetchArticleHomepageHook}
              articlesContainerRef={articlesContainerRef}
            />
          </div>

          {/* Sidebar Area */}
          <div className="d-lg-block col-lg-3 col-xl-3 px-xl-0">
            <StickyBox offsetTop={20}>
              {/* Square Banner for non-tablet devices */}
              {!isTablet && <SquareIframeHomepage />}
              <SidebarArticles
                categories={[
                  'angebote',
                  'bildung',
                  'gesundekommune',
                  'projekte',
                  'goodnews',
                  'wissen',
                ]}
                calendarContent={calendarContent}
              />
            </StickyBox>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePageLayout;
